<template>
  <div class="container">
    <p>
      Diseña e implementa chatbots por tus canales activos.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
    </p>

    <chatbots-controls @search="fetchData" />

    <CustomScrollbar class="scroll">
      <a-spin :spinning="loading">
        <div class="chatbots-list">
          <ChatbotCardCreator @click="handleShowCreate" />

          <template v-for="chatbot in chatbotsList" :key="chatbot?.id">
            <ChatbotCard :chatbotData="chatbot" @update="fetchData" />
          </template>
        </div>
      </a-spin>
    </CustomScrollbar>
  </div>

  <ModalCreateChatbot
    v-if="showModal"
    :visible="showModal"
    @closeModal="showModal = false"
    @update="fetchData"
  />

  <RestrictionChangePlanModal
    title="Realiza un upgrade y accede a todo el poder de Beex."
    v-model:open="openRestriction"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan] }} no incluye este módulo.
      Solicita un upgrade tu plan para potenciar tu ambiente de trabajo.
    </template>
  </RestrictionChangePlanModal>
</template>

<script>
import { ref, computed, defineComponent, provide, reactive, toRaw, onMounted, watch, onUnmounted } from "vue";
import { useStore } from "vuex";

import ChatbotsControls from "@/app/chatbots/components/controls/ChatbotsControls.vue";
import ModalCreateChatbot from "@/app/chatbots/components/modals/ModalCreateChatbot.vue";
import ChatbotCardCreator from "@/app/chatbots/components/cards/ChatbotCardCreator.vue";
import ChatbotCard from "../cards/ChatbotCard.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { ActionPlanStrategy } from "@/app/common/utils/enums";
import EventBus from "@/app/shared/utils/eventBus";

export default defineComponent({
  components: {
    ChatbotsControls,
    ModalCreateChatbot,
    ChatbotCardCreator,
    ChatbotCard,
    CustomScrollbar,
    RestrictionChangePlanModal,
  },
  setup() {
    const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/flujos/chatbots-1/`;
    const store = useStore();

    const user = computed(() => store.getters["getDataUser"]);
    const plan = computed(() => user.value?.change_custom_plan?.plan);
    const openRestriction = ref(false);
    const loading = ref(false);

    const validateActionPlanHeader = () => {
      if (plan.value == PlanType.Voice) {
        store.commit("SET_ACTION_PLAN_HEADER", ActionPlanStrategy.Upgrade);
      } else {
        store.commit("SET_ACTION_PLAN_HEADER", null);
      }
    };

    EventBus.on("SHOW_RESTRICTION_PLAN", () => {
      openRestriction.value = true
    });

    watch(user, () => {
      validateActionPlanHeader();
    })

    watch(plan, () => {
      validateActionPlanHeader();
    })

    const filtersState = reactive({
      name: "",
      channel: 0,
      status: 0,
    });
    provide("filtersState", filtersState);

    const chatbotsList = computed(() => store.getters["getChatbotsList"].results);

    const showModal = ref(false);

    onMounted(async () => {
      validateActionPlanHeader();
      await fetchData();
    });

    const fetchData = async () => {
      const filters = toRaw(filtersState);
      loading.value = true;
      await store.dispatch("fetchChatbotsList", filters);
      loading.value = false;
    };

    const handleShowCreate = () => {
      if (plan.value == PlanType.Voice)
        openRestriction.value = true
      else
        showModal.value = true
    }

    onUnmounted(() => {
      store.commit("SET_ACTION_PLAN_HEADER", null);
    });

    return {
      chatbotsList,
      showModal,
      fetchData,
      DOCS_URL,
      handleShowCreate,
      openRestriction,
      plan,
      PlanTypeText,
      loading,
    };
  },
});
</script>

<style lang="sass" scoped>
.container
  padding: 12px 20px
  height: 100%

.chatbots-list
  gap: 12px
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr))

.scroll
  height: calc(100% - 80px)
  margin: 12px 0px
</style>
